import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'

import { useAppStore } from '@/store'
import { ILoanDetail } from '@/types'
import { formatCents } from '@/utils/common'
import { LoanService } from '@/api/services/LoanService'
import { ProductService } from '@/api/services/ProductService'
import SearchLogo from '@/assets/search.svg'
import { Accordion } from '@/components/UI/Accordion'
import { LoanSummary } from './LoanSummary'
import { ErrorPage } from '@/pages/Error'
import { Chip } from '../UI/Chip'

export const Loans = () => {
  const [expanded, setExpanded] = useState<number | boolean>()
  const [selectedProductKey, setSelectedProductKey] = useState('')
  const { user, setSelectedLoan } = useAppStore()
  const userId = user?.id

  useEffect(() => {
    setExpanded(false)
    setSelectedProductKey('')
  }, [userId])

  const {
    data: loans,
    isLoading: loansLoading,
    isError: loansError,
  } = useQuery<ILoanDetail[]>({
    queryKey: ['loans', userId],
    queryFn: () => LoanService.fetchLoanDetails(),
    enabled: !!userId
  })

  const {
    data: products,
    isLoading: areProductsLoading,
    isError: areProductsError,
  } = useQuery({
    queryKey: ['products'],
    queryFn: () => ProductService.fetchProducts(),
    staleTime: 5 * 60 * 1000,
    enabled: (loans?.length ?? 0) > 0,
  })

  const productKeys = (products ?? []).map(product => product.productKey)

  const handleProductFilterChange = (event: SelectChangeEvent) => {
    setExpanded(false)
    setSelectedProductKey(event.target.value)
  }

  const handleAccordionChange = (loan: ILoanDetail) => {
    setExpanded(prev => (prev === loan.loanId ? false : loan.loanId))
  }

  const handleLoanClick = (loan: ILoanDetail) => {
    setSelectedLoan(loan)
  }

  const renderLoanAccordion = (loan: ILoanDetail) => {
    return (
      <Box
        key={loan.loanId}
        mb={2}
        p={2}
        sx={{
          border: '1px solid #E4E7EC',
          borderRadius: '12px',
          cursor: 'pointer',
          minWidth: '280px',
        }}
        onClick={() => handleLoanClick(loan)}
      >
        <ResponsiveRow>
          <StyledLink pb={1} sx={{ cursor: 'default' }}>
            {`Loan Reference: ${loan.loanReference}`}
          </StyledLink>
          <Stack direction="row" gap={1}>
            <Chip value={loan.status.toLowerCase()} />
            <Chip value={loan.subStatus.toLowerCase()} />
          </Stack>
        </ResponsiveRow>

        <Accordion
          expanded={expanded === loan.loanId}
          onChange={() => handleAccordionChange(loan)}
          summary={
            <StyledBox>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 10 }}>
                <Box display="flex" alignItems="center">
                  <Label>Loan Id</Label>
                  <Value pl={1}>{loan.loanId}</Value>
                </Box>

                <Box display="flex" alignItems="center">
                  <Label>Loan Amount</Label>
                  <Value pl={1}>{formatCents(loan.amount)}</Value>
                </Box>
              </Stack>
            </StyledBox>
          }
          content={<LoanSummary loan={loan} />}
        />
      </Box>
    )
  }

  const filteredLoans = selectedProductKey
    ? (loans ?? []).filter(loan => loan.productKey === selectedProductKey)
    : (loans ?? [])
  const sortedLoans = [...filteredLoans].sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  return (
    <Box sx={{ minWidth: '100%', overflowX: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
        <Typography variant="h6">Loans</Typography>
        {(loans?.length ?? 0) > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={areProductsError ? 'Unable to load product keys' : 'Filter'}>
              <FilterAltOutlinedIcon sx={{ fontSize: 20, color: '#344054' }} />
            </Tooltip>
            <Stack gap={0.5}>
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: '#344054', lineHeight: '20px' }}
              >
                By Product Key
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedProductKey}
                  onChange={handleProductFilterChange}
                  displayEmpty
                  disabled={areProductsLoading || areProductsError}
                  inputProps={{ 'aria-label': 'Filter by product key' }}
                  sx={{ borderRadius: '8px', fontSize: 14, color: '#344054' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {productKeys.map(productKey => (
                    <MenuItem key={productKey} value={productKey}>
                      {productKey}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
        )}
      </Box>

      {loansLoading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress size={25} sx={{ color: 'lightgray' }} />
        </Box>
      ) : loansError ? (
        <ErrorPage />
      ) : sortedLoans.length === 0 ? (
        <EmptyContainer>
          <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
          <Title>
            {selectedProductKey ? 'No loans found for the selected product' : 'No Loans Found'}
          </Title>
          <Message>{!selectedProductKey && "The customer hasn't added any loan yet"}</Message>
        </EmptyContainer>
      ) : (
        sortedLoans.map((loan: ILoanDetail) => renderLoanAccordion(loan))
      )}
    </Box>
  )
}

const StyledBox = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  paddingLeft: theme.spacing(1),
}))

const Label = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#344054',
}))

const Value = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 'normal',
  fontFamily: 'Inter',
  color: '#475467',
  wordBreak: 'break-word',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const EmptyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: `1px solid #E4E7EC`,
  borderRadius: '8px',
  padding: '30px 16px',
  textAlign: 'center',

  [theme.breakpoints.down('sm')]: {
    padding: '20px 10px',
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
}))

const StyledLink = styled(Typography)(({ theme }) => ({
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  '&:hover': {
    color: '#475467',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}))

const ResponsiveRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    paddingBottom: 10,
  },
}))
